import Dexie from "dexie";
import { SymmKey } from "src/common/keys";
import { FSMessage, FSType } from "src/common/keys/protos/collections_pb";
import {
  Account, PermissionSetType, getEnumKey, DriveEntryTypes, decryptItemName, getDirSymmKey, Grant, dayjs, SearchNodeBase, SearchNode,
  SearchBase, NodeIdentifier, DirectoryPaginationInfo
} from "src/common";
import { SearchDatabase, SCHEMA_VERSION } from "./db";
import { FilenameTokenizer } from "./search-tokenizer.class";
import _ from "lodash";

export class SearchClient {
    databases: Map<string, SearchDatabase> = new Map<string, SearchDatabase>();
    db!: SearchDatabase;
    readonly tokenizer = new FilenameTokenizer();
    abort_token = false;
    node_identifier!: NodeIdentifier;
    constructor(user_id: string) {
        this.databases = new Map<string, SearchDatabase>();
        this._setCurrentUser(user_id);
    }

    // Description: Retrieve current db from the map OR create a new DB with current_account.user_id
    private async _setCurrentUser(user_id: string) {
        await this.handleValidateVersion(user_id);
        let db = this.databases.has(user_id) ? this.databases.get(user_id) : undefined;
        if (!db) {
            db = new SearchDatabase(user_id);
            this.databases.set(user_id, db);
        }
        db.metadata.put({ key: "version", value: SCHEMA_VERSION });
        this.db = db;
    }

    // Description: Check metadata version in the actual table before init the table
    private async handleValidateVersion(user_id: string) {
        const saved_db = new Dexie(user_id);
        saved_db.version(1).stores({ metadata: "&key" });
        const meta = saved_db.table("metadata");
        const schema_version = await meta.get("version");
        if (!schema_version || schema_version.value < SCHEMA_VERSION) {
            await Dexie.delete(user_id);
        }
    }

    // Description: Set Current Directory being search
    public async setCurrentSearch(current_node: NodeIdentifier) {
        await this.db.metadata.put({ key: "current_node", current_node }).then(() => {
            return Promise.resolve();
        }).catch(Dexie.BulkError, (e) => { console.log("Error Inserting new search values: ", e); });
    }

    // Description: Get Current Directory being search
    public async getCurrentSearch(): Promise<NodeIdentifier | undefined> {
        const result = await this.db.metadata.get("current_node");
        return result?.current_node;
    }

    // Description: Delete User's IndexedDB
    public async deleteUserDB(user_id: string) {
        return await Dexie.delete(user_id).then(() => {
            return true;
        }).catch((error) => {
            console.log(`ERROR Deleting User's IndexedDB for ${user_id}: `, error);
        });
    }

    // GLOBAL DB Fns
    // -------------------------------------------------------------------------------------------------------------------
    // Description: set current node identifier for current directory scoped search and set abort to false
    public initCurrentNode(node_identifier: NodeIdentifier): void {
        this.abort_token = false;
        this.node_identifier = node_identifier;
    }

    // Description: Initialize the SearchNodeBase from Dir Entries
    public async initDBNodes(
        collection_id: string,
        dir: FSMessage.Dir,
        current_account: Account,
        permissions: PermissionSetType,
        grant?: Grant
    ): Promise<SearchNodeBase[] | undefined> {
        const id = dir.getId_asB64();
        const entry_list = dir.getEntriesList();
        const dir_symm_key = dir.getWrappedDirKey();
        const reader_user_key = !!grant ? await grant.key(current_account) : undefined;
        const symmetric_key = !!dir_symm_key ? await getDirSymmKey(dir_symm_key, current_account, permissions, reader_user_key) : undefined;
        if (!!symmetric_key && !this.abort_token) {
            return await this.addDecryptedEntries(collection_id, id, entry_list, symmetric_key);
        }
    }

    // Description: Add Decrypted Entries to IndexedDB
    public async addDecryptedEntries(collection_id: string, parent_id: string, entry_list: FSMessage.Dir.Entry[], directory_symmetric_key: SymmKey) {
        const nodes = _.compact(await Promise.all(_.map(entry_list, async (entry: FSMessage.Dir.Entry) => {
            // NOTE: Do not call if the search was destroyed
            if (!this.abort_token) {
                const _date = entry.getLastUpdate();
                const latest_date = entry.hasLastLocalFsUpdate() && dayjs(entry.getLastLocalFsUpdate()).isValid() ? dayjs.utc(entry.getLastLocalFsUpdate()).local() :
                    !!_date ? dayjs.unix(_date) : undefined;
                const decoded_name = await decryptItemName(entry.getName_asU8(), directory_symmetric_key) || "";
                const type = getEnumKey(FSType, entry.getType()) as DriveEntryTypes || null;
                const search_node: SearchNodeBase = {
                    id: entry.getId_asB64(), // NOTE: THIS IS THE DIRECTORY ID for root_node entry.getId_asB64() OR Helpers.b64Encode(entry.getId_asU8(), true)
                    collection_id,
                    linked_collection_id: entry.getLinkedCollectionId_asB64(),
                    name: decoded_name,
                    type,
                    last_modification_date: !!latest_date ? latest_date.format("MM/DD/YYYY h:mm A") : "",
                    parent_id,
                    name_tokens: this.tokenizer.getTokens(decoded_name)
                };

                return new SearchNode(search_node);
            }
        })));
        await this.addBulkItemsToIndex(nodes);
        return nodes;
    }

    // Description: Delete all data to start over
    public async destroySearch(abort: boolean = true): Promise<void> {
        this.abort_token = abort;
        await this.db.nodes.clear().then(() => {
            return true;
        }).catch(Dexie.BulkError, (e) => { console.log("ERROR Clearing the nodes table: ", e); });
        await this.db.search.clear().then(() => {
            return true;
        }).catch(Dexie.BulkError, (e) => { console.log("ERROR Clearing the search table: ", e); });
        await this.db.metadata.delete("current_node").then(() => {
            return Promise.resolve();
        }).catch(Dexie.BulkError, (e) => { console.log("ERROR deleting current directory information: ", e); });
    }

    // -------------------------------------------------------------------------------------------------------------------
    // Node tables
    // -------------------------------------------------------------------------------------------------------------------
    // Description: Add single node to DB
    public async addItemToIndex(node: SearchNode): Promise<void> {
        await this.db.nodes.put(node).then(() => {
            return Promise.resolve();
        }).catch(Dexie.BulkError, (e) => { console.log("Some single inserts failed: ", e); });
    }

    // Description: Bulk Put nodes to the table
    // NOTE: Faster than bulkAdd and updates value when key exists
    public async putBulkItemsToIndex(nodes: SearchNode[]) {
        await this.db.nodes.bulkPut(nodes)
            .then(() => {
                return Promise.resolve();
            })
            .catch(Dexie.BulkError, (e) => { console.log("Some inserts failed: ", e); });
    }

    // Description: Bulk Add nodes to the table
    public async addBulkItemsToIndex(nodes: SearchNode[]) {
        await this.db.nodes.bulkAdd(nodes)
            .then(() => {
                return Promise.resolve();
            })
            .catch(Dexie.BulkError, (e) => { console.log("Some inserts failed: ", e); });
    }

    // Description: Delete items by id 
    public async deleteItemsByCollectionId(collection_id: string): Promise<number> {
        const result = await this.db.nodes.where("collection_id").equals(collection_id).delete();
        return result;
    }

    // Description: Delete items by both collection_id and id
    public async deleteItemsByNodeIdentifier(collection_id: string, parent_id: string): Promise<boolean> {
        try {
            const nodes = await this.db.nodes.where({ collection_id, parent_id }).toArray();
            const nodes_ids = nodes.map(node => [node.collection_id, node.id]);
            nodes_ids.length > 0 &&
                await this.db.nodes.bulkDelete(nodes_ids);
            return true;
        } catch (error) {
            return false;
        }
    }

    // Description: Return the total number of node entries in DB
    public async getSearchNodeTotalRowCounts(): Promise<number> {
        const counts = await this.db.nodes.count();
        return counts;
    }

    // Description: Delete all data to start over
    public async destroyNodeTable(): Promise<void> {
        await this.db.nodes.clear().then(() => {
            return true;
        }).catch(Dexie.BulkError, (e) => { console.log("ERROR Clearing the nodes table: ", e); });
    }

    // Description: Delete all node data with exception of a subset
    public async destroyNodeTableSelective(exceptions?: SearchNode[]): Promise<void> {
        await this.db.nodes.clear().then(() => {
            return true;
        }).catch(Dexie.BulkError, (e) => { console.log("ERROR Clearing the nodes table: ", e); });
        !!exceptions && await this.putBulkItemsToIndex(exceptions);
    }

    // -------------------------------------------------------------------------------------------------------------------
    // Search tracking tables
    // -------------------------------------------------------------------------------------------------------------------
    // Description: Save search sequence and params
    //              IF snapshot is undefined then initialize links to Index for later fetch seq = 0
    public async addItemToSearchIndex(collection_id: string, id: string, snapshot?: FSMessage.Snapshot): Promise<void> {
        const search: SearchBase = !!snapshot ? {
            id,
            collection_id,
            sequence: snapshot.getSeq() || 0,
            cursor_sequence: snapshot.getCursorSeq() || 0,
            cursor_sub_sequence: snapshot.getCursorSubseq() || 0,
            has_more: snapshot.hasHasMore() ? 1 : 0
        } : { // NOTE: initialize links 
            id,
            collection_id,
            sequence: 0,
            cursor_sequence: 0,
            cursor_sub_sequence: 0,
            has_more: 1
        };
        await this.db.search.put(search).then(() => Promise.resolve());
    }

    // Description: Save search for specific directories sequence and params
    public async addDirectoryToSearchIndex(collection_id: string, id: string, pagination_info?: DirectoryPaginationInfo): Promise<void> {
        const search: SearchBase = !!pagination_info ? {
            id,
            collection_id,
            sequence: pagination_info.next_seq || 0,
            cursor_sequence: pagination_info.next_seq || 0,
            cursor_sub_sequence: pagination_info.next_subseq || 0,
            has_more: pagination_info.has_more ? 1 : 0
        } : { // NOTE: initialize links 
            id,
            collection_id,
            sequence: 0,
            cursor_sequence: 0,
            cursor_sub_sequence: 0,
            has_more: 1
        };
        await this.db.search.put(search).then(() => Promise.resolve());
    }

    // Description: Return the current version of a search row
    public async getSearchByNodeIdentifiers(collection_id: string, id: string): Promise<SearchBase | undefined> {
        const result = await this.db.search.get([collection_id, id]);
        return result;
    }

    // Description: Return the current version of a search row
    public async getSearchByIdentifiers(collection_id: string): Promise<SearchBase | undefined> {
        const result = await this.db.search.get([collection_id]);
        return result;
    }

    // Description: Delete items by id 
    public async deleteSearchByCollectionId(collection_id: string): Promise<number> {
        const result = await this.db.search.where("collection_id").equals(collection_id).delete();
        return result;
    }
}
